import { useEffect, useRef, useState } from "react";
import LandingNavbar from "./LandingNavbar";
import {
  SectionOne,
  SectionFour,
  SectionThree,
  SectionTwo,
  SectionFive,
} from "./Sections";
import { LandingFooter } from "./LandingNavbar";
import ScrollToPlugin from "gsap/ScrollToPlugin";
import gsap from "gsap";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { getServiceStatus } from "@/features/status/api/status.api";

export default function LandingContainer() {
  const queryClient = useQueryClient();

  useEffect(() => {
    queryClient.prefetchQuery({
      queryKey: ["service-status"],
      queryFn: getServiceStatus,
    });
  }, [queryClient]);

  const [isScrolled, setIsScrolled] = useState(false);

  const sectionFiveRef = useRef<HTMLElement | null>(null);

  const { section } = useSearch({ from: "/" });
  const navigate = useNavigate();

  gsap.registerPlugin(ScrollToPlugin);

  useEffect(() => {
    const scroller = document.querySelector(".landing-scroll");
    if (!scroller) return;

    const onScroll = () => {
      setIsScrolled(scroller.scrollTop > 10);
    };

    scroller.addEventListener("scroll", onScroll);
    return () => scroller.removeEventListener("scroll", onScroll);
  }, []);

  const scrollToSectionFive = () => {
    const scroller = document.querySelector(".landing-scroll");
    if (!scroller || !sectionFiveRef.current) return;

    gsap.to(scroller, {
      scrollTo: {
        y: sectionFiveRef.current,
        offsetY: -80,
      },
      duration: 0.8,
      ease: "power3.inOut",
    });
  };

  useEffect(() => {
    if (section !== "section5") return;

    const timer = setTimeout(() => {
      scrollToSectionFive();

      setTimeout(() => {
        navigate({
          to: "/",
          search: { section: undefined },
          replace: true,
        });
      }, 900);
    }, 100);

    return () => clearTimeout(timer);
  }, [section]);

  return (
    <div className="landing-scroll scrollbar-thin relative flex h-screen w-full flex-col overflow-x-hidden overflow-y-auto scroll-smooth bg-neutral-50">
      <LandingNavbar
        isScrolled={isScrolled}
        getInTouch={true}
        onGetInTouchClick={scrollToSectionFive}
      />

      <div className="-mt-[80px] flex-1">
        <section className="panel flex min-h-dvh w-full flex-col justify-center gap-[120px]">
          <SectionOne onGetInTouchClick={scrollToSectionFive} />
        </section>

        <section className="panel flex h-dvh w-full flex-col items-center justify-center gap-2.5 px-6 pt-[80px] sm:px-12 md:px-20 lg:px-32 xl:px-[200px]">
          <SectionTwo />
        </section>

        <section className="panel flex h-dvh w-full flex-col items-center justify-center gap-2.5 px-6 pt-[80px] sm:px-12 md:px-20 lg:px-32 xl:px-[200px]">
          <SectionThree />
        </section>

        <section className="panel flex h-dvh w-full flex-col items-center justify-center gap-2.5 pt-[80px]">
          <SectionFour />
        </section>

        <section
          className="panel flex h-dvh w-full flex-col items-center justify-center gap-2.5 px-6 pt-[80px] sm:px-12 md:px-20 lg:px-32 xl:px-[200px]"
          ref={sectionFiveRef}
        >
          <SectionFive />
        </section>

        <LandingFooter />
      </div>
    </div>
  );
}
